import React from "react";
import { 
  Shield, ShieldCheck, Trophy, User, Star, AlertTriangle,
  Award, Medal, Crown, Gem, Diamond,
  Users, Heart, Clock, Wrench, Home, Timer, Gift, Feather, Palette
} from "lucide-react";
import { cn } from "../lib/utils";

const levelConfig = {
  citoyen: {
    name: "Citoyen",
    icon: User,
    bgClass: "badge-citoyen",
    color: "#1A1A24"
  },
  gardien: {
    name: "Gardien",
    icon: ShieldCheck,
    bgClass: "badge-gardien",
    color: "#007AFF"
  },
  legende: {
    name: "Légende",
    icon: Trophy,
    bgClass: "badge-legende",
    color: "#FFB703"
  }
};

const badgeConfig = {
  ally: {
    name: "Allié",
    icon: Star,
    bgClass: "badge-ally",
    color: "#34C759"
  },
  legend_rated: {
    name: "Étoile",
    icon: Star,
    bgClass: "badge-legende",
    color: "#FFB703"
  },
  warning: {
    name: "Prudence",
    icon: AlertTriangle,
    bgClass: "badge-warning",
    color: "#FF3B30"
  },
  // Referral badges
  parrain_bronze: {
    name: "Parrain Bronze",
    icon: Award,
    bgClass: "bg-amber-100 text-amber-700",
    color: "#CD7F32"
  },
  parrain_argent: {
    name: "Parrain Argent",
    icon: Medal,
    bgClass: "bg-slate-100 text-slate-600",
    color: "#C0C0C0"
  },
  parrain_or: {
    name: "Parrain Or",
    icon: Crown,
    bgClass: "bg-yellow-100 text-yellow-700",
    color: "#FFD700"
  },
  parrain_platine: {
    name: "Parrain Platine",
    icon: Gem,
    bgClass: "bg-purple-100 text-purple-700",
    color: "#E5E4E2"
  },
  parrain_diamant: {
    name: "Parrain Diamant",
    icon: Diamond,
    bgClass: "bg-cyan-100 text-cyan-700",
    color: "#B9F2FF"
  }
};

// Skill badges configuration
const skillBadgeConfig = {
  communautaire: {
    name: "Communautaire",
    icon: Users,
    bgClass: "bg-blue-100 text-blue-700",
    description: "Actif dans la communauté"
  },
  serviable: {
    name: "Serviable",
    icon: Heart,
    bgClass: "bg-pink-100 text-pink-700",
    description: "Toujours prêt à aider"
  },
  disponible: {
    name: "Disponible",
    icon: Clock,
    bgClass: "bg-green-100 text-green-700",
    description: "Réactif et accessible"
  },
  bricoleur: {
    name: "Bricoleur",
    icon: Wrench,
    bgClass: "bg-orange-100 text-orange-700",
    description: "Expert en réparations"
  },
  accueillant: {
    name: "Accueillant",
    icon: Home,
    bgClass: "bg-teal-100 text-teal-700",
    description: "Chaleureux et ouvert"
  },
  ponctuel: {
    name: "Ponctuel",
    icon: Timer,
    bgClass: "bg-indigo-100 text-indigo-700",
    description: "Toujours à l'heure"
  },
  genereux: {
    name: "Généreux",
    icon: Gift,
    bgClass: "bg-red-100 text-red-700",
    description: "Partage sans compter"
  },
  fiable: {
    name: "Fiable",
    icon: Shield,
    bgClass: "bg-emerald-100 text-emerald-700",
    description: "On peut compter sur lui"
  },
  patient: {
    name: "Patient",
    icon: Feather,
    bgClass: "bg-sky-100 text-sky-700",
    description: "Calme et compréhensif"
  },
  createur: {
    name: "Créateur",
    icon: Palette,
    bgClass: "bg-fuchsia-100 text-fuchsia-700",
    description: "Talent créatif"
  }
};

export { skillBadgeConfig };

export const LevelBadge = ({ level, size = "default", showLabel = true }) => {
  const config = levelConfig[level] || levelConfig.citoyen;
  const Icon = config.icon;
  
  const sizeClasses = {
    small: "px-2 py-0.5 text-xs gap-1",
    default: "px-3 py-1 text-sm gap-1.5",
    large: "px-4 py-2 text-base gap-2"
  };
  
  const iconSizes = {
    small: 12,
    default: 16,
    large: 20
  };

  return (
    <span 
      className={cn(
        "inline-flex items-center font-bold rounded-full",
        config.bgClass,
        sizeClasses[size]
      )}
      data-testid={`level-badge-${level}`}
    >
      <Icon size={iconSizes[size]} />
      {showLabel && <span>{config.name}</span>}
    </span>
  );
};

export const UserBadge = ({ badge, size = "small" }) => {
  const config = badgeConfig[badge];
  if (!config) return null;
  
  const Icon = config.icon;
  
  const sizeClasses = {
    small: "px-2 py-0.5 text-xs gap-1",
    default: "px-3 py-1 text-sm gap-1.5"
  };
  
  const iconSizes = {
    small: 10,
    default: 14
  };

  return (
    <span 
      className={cn(
        "inline-flex items-center font-bold rounded-full",
        config.bgClass,
        sizeClasses[size]
      )}
      data-testid={`user-badge-${badge}`}
    >
      <Icon size={iconSizes[size]} />
      <span>{config.name}</span>
    </span>
  );
};

export const BadgeList = ({ badges = [], size = "small" }) => {
  if (!badges || badges.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-1" data-testid="badge-list">
      {badges.map((badge) => (
        <UserBadge key={badge} badge={badge} size={size} />
      ))}
    </div>
  );
};

// Skill Badge component
export const SkillBadge = ({ badge, size = "small", showDescription = false }) => {
  const config = skillBadgeConfig[badge];
  if (!config) return null;
  
  const Icon = config.icon;
  
  const sizeClasses = {
    small: "px-2 py-0.5 text-xs gap-1",
    default: "px-3 py-1 text-sm gap-1.5"
  };
  
  const iconSizes = {
    small: 10,
    default: 14
  };

  return (
    <span 
      className={cn(
        "inline-flex items-center font-medium rounded-full",
        config.bgClass,
        sizeClasses[size]
      )}
      title={config.description}
      data-testid={`skill-badge-${badge}`}
    >
      <Icon size={iconSizes[size]} />
      <span>{config.name}</span>
    </span>
  );
};

// Skill Badge List
export const SkillBadgeList = ({ badges = [], size = "small" }) => {
  if (!badges || badges.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-1" data-testid="skill-badge-list">
      {badges.map((badge) => (
        <SkillBadge key={badge} badge={badge} size={size} />
      ))}
    </div>
  );
};

export const LevelProgress = ({ 
  currentLevel, 
  nextLevel, 
  currentCount, 
  requiredCount, 
  progressPercent 
}) => {
  const currentConfig = levelConfig[currentLevel] || levelConfig.citoyen;
  const nextConfig = nextLevel ? levelConfig[nextLevel] : null;
  
  return (
    <div className="space-y-3" data-testid="level-progress">
      <div className="flex items-center justify-between">
        <LevelBadge level={currentLevel} size="default" />
        {nextConfig && (
          <span className="text-sm text-muted-foreground">
            {currentCount}/{requiredCount} vers {nextConfig.name}
          </span>
        )}
      </div>
      
      <div className="progress-bar-container">
        <div 
          className="progress-bar-fill"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      
      {nextConfig && progressPercent < 100 && (
        <p className="text-xs text-muted-foreground text-center">
          Recevez {requiredCount - currentCount} note{requiredCount - currentCount > 1 ? 's' : ''} positive{requiredCount - currentCount > 1 ? 's' : ''} pour devenir {nextConfig.name}
        </p>
      )}
      
      {progressPercent >= 100 && !nextLevel && (
        <p className="text-xs text-center font-medium text-amber-600">
          🏆 Vous êtes au niveau maximum !
        </p>
      )}
    </div>
  );
};

export const LevelCard = ({ user, progress }) => {
  const level = user?.level || "citoyen";
  const cardClass = level === "legende" 
    ? "level-card-legende" 
    : level === "gardien" 
      ? "level-card-gardien" 
      : "level-card";

  return (
    <div className={cn("level-card", cardClass)} data-testid="level-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold mb-1">Votre Niveau</h3>
          <LevelBadge level={level} size="large" />
        </div>
        <div className="text-right">
          <div className="text-3xl font-extrabold" style={{ fontFamily: 'Outfit' }}>
            {user?.average_rating?.toFixed(1) || "0.0"}
          </div>
          <div className="text-sm text-muted-foreground">
            {user?.total_ratings || 0} avis
          </div>
        </div>
      </div>
      
      {progress && (
        <LevelProgress {...progress} />
      )}
      
      {!user?.can_rate && (
        <div className="mt-4 p-3 bg-white/50 rounded-xl text-sm">
          <p className="text-muted-foreground">
            <Shield className="inline w-4 h-4 mr-1" />
            Recevez 3 avis positifs pour débloquer le droit d'évaluer
          </p>
        </div>
      )}
    </div>
  );
};
